// Schedule's Map, Blueprint only (pages/ux-full schedule/map), on ListPage (BL-189): today's visits on the
// map (DayMap, or Google's map with a maps key) with the visits it can't place under it (MapUnplaced),
// and beside it the visit picked (VisitCard) over where everyone is (RowList). Reads dispatch.board and
// re-reads on the board's live changes. Team manager's Who's working and Time off are TeamScreen.tsx
// and TimeOffScreen.tsx.
import { useMemo, useState } from 'react';
import { Button, Link, MapUnplaced, RowList, teamRows, VisitCard } from '@flowology/ui-web/fl';
import { ListPage, Section, type ListStatus } from '@flowology/ui-web/patterns';
import { DayMapOrGoogle } from './DayMapOrGoogle.tsx';
import { dayVisits, type Board } from '../board-model.ts';
import { openJob } from '../job/job-page.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { todayIn } from '../books/model.ts';
import { useQuery } from '../books/ui.tsx';
import './schedule-pages.css';
import { useLive, useNow } from './live.ts';
import { dayMapProps, mapVisitCard, pensBy, teamRow } from './pages-model.ts';

/** How many of the team show before Show all. */
const FIRST = 5;

// ── Map ──

export function MapScreen({
  nav,
  timeZone,
  onBoard,
}: {
  nav: NavProps;
  timeZone: string;
  /** The day board on this day, with a visit open when one is given. */
  onBoard: (date: string, visit?: string) => void;
}) {
  const today = todayIn(timeZone);
  const now = useNow();
  const board = useQuery<Board>('dispatch.board', { date: today });
  useLive(() => void board.reload());
  const [pick, setPick] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const crumb = usePageCrumb(nav);
  const b = board.data && board.data.date === today ? board.data : null;
  const pens = useMemo(() => (b ? pensBy(b.technicians) : new Map<string, number>()), [b]);
  const map = b ? dayMapProps(b, pens, now) : null;
  // the one open: picked, while it's still today's
  const sel = b && pick ? (dayVisits(b).find((v) => v.appointmentId === pick) ?? null) : null;
  // where everyone is: what the board knows (on a job, on the way, away, free, off), never a live position
  const rows = b ? b.technicians.map((t) => teamRow(t, pens.get(t.id) ?? null, [], b, today, now)) : [];
  const count = map ? map.pins.length + map.unplaced.length : 0;
  const status: ListStatus = map ? (count ? 'ready' : 'empty') : board.error ? 'failed' : 'loading';
  return (
    <div className="app">
      <main className="bp-map-main">
        <PageBanners nav={nav} />
        <ListPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: 'Today on the map',
            lede: map
              ? `${count} ${count === 1 ? 'visit' : 'visits'} today, each technician’s route in their pen. Tap a pin.${map.unplaced.length ? ` ${map.unplaced.length} ${map.unplaced.length === 1 ? 'has' : 'have'} no location for the address.` : ''}`
              : 'Where today’s visits are, each technician’s route in their pen.',
            actions: <Link onPress={() => onBoard(today)}>The board</Link>,
          }}
          list={{
            title: 'Today’s visits',
            status,
            what: 'today’s visits',
            emptyText: 'Nothing booked today. Visits booked for today show here.',
            failedText: board.error ?? 'Today’s visits didn’t load.',
            onRetry: () => void board.reload(),
            children: map ? (
              <>
                <DayMapOrGoogle
                  data={map}
                  label="Today’s visits on the map"
                  none="None of today’s visits has a location for its address."
                  selected={pick}
                  onPick={setPick}
                />
                {/* the visits with no point, with the map they're missing from: never dropped */}
                {map.unplaced.length > 0 && <MapUnplaced data={map} selected={pick} onPick={setPick} />}
              </>
            ) : null,
          }}
          {...(b
            ? {
                aside: {
                  before: sel ? (
                    <Section title="The visit">
                      <VisitCard visit={mapVisitCard(sel, b, pens, now)} />
                      <div className="bp-map-acts">
                        <Button variant="primary" onPress={() => onBoard(today, sel.appointmentId)}>
                          Open on the board
                        </Button>
                        <Link onPress={() => openJob(sel.jobId)} label={`Open job #${sel.jobNumber}`}>
                          Open the job
                        </Link>
                      </div>
                    </Section>
                  ) : count > 0 ? (
                    <VisitCard
                      visit={null}
                      empty={
                        <>
                          <b>Tap a pin.</b> The visit shows here, with what you can do next.
                        </>
                      }
                    />
                  ) : null,
                  title: 'Where everyone is',
                  status: rows.length ? 'ready' : 'empty',
                  emptyText: 'No technicians yet. The owner adds them under Setup › Company.',
                  children: (
                    <>
                      <RowList rows={teamRows({ techs: all ? rows : rows.slice(0, FIRST) })} />
                      {rows.length > FIRST && !all && (
                        <p className="bp-map-more">
                          <Link onPress={() => setAll(true)}>Show all {rows.length}</Link>
                        </p>
                      )}
                    </>
                  ),
                  after: (
                    <p className="bp-map-sub">
                      From the board: the visit each is on or driving to, and their time away. Nothing tracks
                      where a technician is between visits.
                    </p>
                  ),
                },
              }
            : {})}
        />
      </main>
    </div>
  );
}
